// TODO - saving this file with fast reload causes the app to crash because SessionContext gets lost throughout the component tree

import { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react'
import { hideActiveTool, isToolActive } from '../../utils/toolUtils'
import { SButton, SDialog, SIcon, SText, SView } from '../scaled'
import { ExplorerConfigContext } from './ExplorerConfigProvider'
import { SessionContext } from './SessionContextProvider'

interface Props {
  children: ReactNode
}

/** (ms) time of inactivity to be idle */
const idleTimeout = 30 * 1000
/** (ms) time of inactivity to show screensaver */
const screensaverTimeout = 60 * 1000
/** (ms) time of inactivity to prompt session to end */
const sessionTimeout = 5 * 60 * 1000
/** (ms) time after timeout prompt to auto end the session */
const autoSessionEndTimeout = 30 * 1000

export interface ITimeoutContext {
  isIdle: boolean
}

export const TimeoutContext = createContext<ITimeoutContext>({} as any)

export function TimeoutProvider({ children }: Props) {
  const { endSession, status, updateStatus, routeSteps, startSession } = useContext(SessionContext)
  const { theme } = useContext(ExplorerConfigContext)
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false)
  const [isIdle, setIsIdle] = useState(false)

  useEffect(() => {
    resetTimer()
  }, [status, routeSteps])

  const idle = useRef<Timer>(undefined)
  const screenSaver = useRef<Timer>(undefined)
  const session = useRef<Timer>(undefined)
  const autoSession = useRef<Timer>(undefined)

  function resetTimer() {
    if (showTimeoutWarning) setShowTimeoutWarning(false)
    if (isIdle) setIsIdle(false)

    clearTimeout(idle.current)
    clearTimeout(screenSaver.current)
    clearTimeout(session.current)
    clearTimeout(autoSession.current)

    if (status === 'init' || status === 'screensaver') return

    idle.current = setTimeout(() => setIsIdle(true), idleTimeout)
    const atStart = status === 'active' && routeSteps.length === 0
    if (atStart) {
      screenSaver.current = setTimeout(() => {
        updateStatus('screensaver')
      }, screensaverTimeout)
    } else {
      session.current = setTimeout(() => {
        setShowTimeoutWarning(true)
        autoSession.current = setTimeout(() => {
          setShowTimeoutWarning(false)
          endSession('timeout-button')
        }, autoSessionEndTimeout)
      }, sessionTimeout)
    }
  }

  function handleClick(e: React.MouseEvent) {
    if (isToolActive()) {
      e.stopPropagation()
      hideActiveTool()
    }

    resetTimer()
    if (status === 'screensaver') startSession()
  }

  return (
    <TimeoutContext.Provider value={{ isIdle }}>
      <SView flex={1} userSelect="none" onClickCapture={handleClick}>
        {children}

        <SDialog w={500} open={showTimeoutWarning} onClose={resetTimer}>
          <SView p={40} gap={40} align="center">
            <SText textAlign="center" fontSize={50}>
              Are you still there?
            </SText>

            <SText textAlign="center" w={320} fontSize={30}>
              If so, please continue your session.
            </SText>

            <SView alignSelf="stretch" direction="row" justifyContent="space-between">
              <SButton
                variant="outline"
                onClick={() => endSession('timeout-auto')}
                LeftIcon={(p: any) => <SIcon value="chevron-left" {...p} />}
              >
                END
              </SButton>
              <SButton
                backgroundColor={theme.color.primary}
                color="black"
                variant="solid"
                onClick={resetTimer}
                RightIcon={(p: any) => <SIcon value="chevron-right" {...p} />}
              >
                CONTINUE
              </SButton>
            </SView>
          </SView>
        </SDialog>
      </SView>
    </TimeoutContext.Provider>
  )
}
